import { memo, useState, useEffect } from "react";
import { Button } from "@mui/material";
import { ROLE_TYPE, REQUEST_STATUS } from "../../constants/constants";
import { BodyText } from "../../theme/Styles";
import { replaceGuionToSlashFromString, sanitizeHTML } from "../../utilities/functions/StringUtil";
import { statusColors, variantStatus, getStatusVariant } from "./RequestCardConstants";
import LocalStorageService from "../../services/LocalStorageService";
import {
    ButtonContainer,
    Container,
    ActionRequiredContainer,
    ProgressBarContainer,
    ProgressBarPercent,
    ProgressHeader,
    ProgressStatusText,
    ProgressPercentText,
    SolutionAlert,
    SolutionText,
    StyledWarningIcon,
    Title,
    CardContent,
    CardBody,
    MetaGrid,
    DesktopOnlyMeta,
    ProgressContainer,
    SubTitle,
    ActionRequiredMessageBox
} from "./styles/RequestCardStyles";

function RequestCard({
    isGridView = false,
    statusID,
    rolBase,
    title,
    citizenID,
    projectName,
    date,
    updatedAt,
    requestCode,
    status,
    company_user_rol,
    onClick,
    onClaimModalVisibility,
    percent,
    actionRequired,
    requiredActionMessage,
    solution,
    solutionColor,
    docIdentification,
    companyName,
    companyRnc,
    companyID,
    approvalNumber,
    handleCompanySelected,
    code,
    setRequestCode,
    userName,
}) {
    const [currentLang, setCurrentLang] = useState(LocalStorageService.getItem('language') || 'es');

    useEffect(() => {
        const handleLangChange = (e) => {
            if (e?.detail) {
                setCurrentLang(e.detail);
            } else {
                setCurrentLang(LocalStorageService.getItem('language') || 'es');
            }
        };
        window.addEventListener('languageChange', handleLangChange);
        return () => window.removeEventListener('languageChange', handleLangChange);
    }, []);

    const isEn = currentLang === 'en';

    const isApprovedStatus = statusID === REQUEST_STATUS.APPROVED || statusID === REQUEST_STATUS.FINISHED || String(status || '').toLowerCase().includes('aprobad') || String(status || '').toLowerCase().includes('completad');
    const isCanceledStatus = statusID === REQUEST_STATUS.CANCELED || String(status || '').toLowerCase().includes('cancelad');
    const isFinishedStatus = isCanceledStatus || statusID === REQUEST_STATUS.REJECTED || isApprovedStatus;

    const hasPendingAction = !isFinishedStatus && Boolean(requiredActionMessage || (actionRequired !== null && actionRequired !== undefined));

    const mainButtonText = isApprovedStatus
        ? (isEn ? "DOWNLOAD RESOLUTION" : "DESCARGAR RESOLUCIÓN")
        : isFinishedStatus
        ? (isEn ? "VIEW DETAILS" : "VER DETALLE")
        : hasPendingAction
        ? (isEn ? "RESPOND" : "RESPONDER")
        : (isEn ? "TRACK STATUS" : "DAR SEGUIMIENTO");

    const currentStatusVariant = getStatusVariant(status, statusID);

    const displayPercent = statusID === 8 || statusID === 10 ? "100%" : percent;

    const formattedDate = date ? new Date(replaceGuionToSlashFromString(date)).toLocaleDateString() : "";
    const formattedUpdatedAt = updatedAt ? new Date(replaceGuionToSlashFromString(updatedAt)).toLocaleDateString() : "";

    const cleanHTML = (htmlStr) => {
        if (!htmlStr) return "";
        return sanitizeHTML(htmlStr);
    };

    const cardContentJSX = (
        <CardContent
            sx={{
                p: isGridView ? '18px !important' : undefined,
                height: isGridView ? '100%' : undefined,
                display: isGridView ? 'flex' : undefined,
                flexDirection: isGridView ? 'column' : undefined,
                justifyContent: isGridView ? 'space-between' : undefined,
                boxSizing: 'border-box'
            }}
        >
            <div style={{ display: 'flex', flexDirection: 'column', gap: isGridView ? '14px' : '20px', flex: isGridView ? 1 : undefined }}>
                {isCanceledStatus ? (
                    <div style={{
                        padding: "12px 16px",
                        backgroundColor: "#FEF2F2",
                        border: "1px solid #FECACA",
                        borderRadius: "10px",
                        display: "flex",
                        alignItems: "flex-start",
                        gap: "10px",
                        color: "#991B1B"
                    }}>
                        <StyledWarningIcon style={{ color: "#DC2626", fontSize: "20px", flexShrink: 0, marginTop: "2px" }} />
                        <div style={{ fontSize: "13px", lineHeight: "1.45" }}>
                            <strong style={{ color: "#7F1D1D", display: "block", marginBottom: "2px" }}>{isEn ? "Cancellation Reason:" : "Motivo de Cancelación:"}</strong>
                            {solution ? (
                                <span>{solution}</span>
                            ) : (
                                <span>
                                    {isEn 
                                      ? "Application cancelled due to expiration of the 10-day limit to complete the required action or make the corresponding payment."
                                      : "Solicitud cancelada por vencimiento del plazo límite (10 días) para completar la acción requerida o realizar el pago correspondiente."}
                                    {formattedUpdatedAt && ` (${isEn ? "Notified" : "Notificado"}: ${formattedUpdatedAt})`}
                                </span>
                            )}
                        </div>
                    </div>
                ) : solution !== null && (
                    <DesktopOnlyMeta>
                        <SolutionAlert>
                            <StyledWarningIcon style={{ color: "#b45309", fontSize: "20px" }} />
                            <SolutionText>{solution}</SolutionText>
                        </SolutionAlert>
                    </DesktopOnlyMeta>
                )}

                <div 
                    onClick={onClick}
                    style={{ 
                        display: "flex", 
                        flexDirection: "column", 
                        gap: "2px",
                        cursor: "pointer",
                        width: "fit-content"
                    }}
                >
                    <Title style={{ fontSize: isGridView ? "16px" : undefined }}>{projectName ? projectName : title}</Title>
                    {projectName && <SubTitle style={{ fontSize: isGridView ? "12px" : undefined }}>{isEn ? "Service:" : "Servicio:"} {title}</SubTitle>}
                </div>

                <CardBody style={{ flexDirection: isGridView ? "column" : undefined, gap: isGridView ? "14px" : undefined }}>
                    <MetaGrid style={{ gridTemplateColumns: isGridView ? "1fr" : undefined, gap: isGridView ? "6px" : undefined }}>
                        <BodyText>
                            <strong>{isEn ? "Application No:" : "Solicitud No:"}</strong> {requestCode}
                        </BodyText>
                        <BodyText>
                            <strong>{isEn ? "Status:" : "Estado:"}</strong> {status}
                        </BodyText>
                        {companyName && (
                            <BodyText>
                                <strong>{isEn ? "Company:" : "Empresa:"}</strong> {companyName} {(!isGridView && companyRnc) ? `(RNC: ${companyRnc})` : ''}
                            </BodyText>
                        )}
                        {!isGridView && companyName && userName && (
                            <DesktopOnlyMeta>
                                <BodyText>
                                    <strong>{isEn ? "Submitted by:" : "Solicitado por:"}</strong> {userName}
                                </BodyText>
                            </DesktopOnlyMeta>
                        )}
                        {!isGridView && approvalNumber && (
                            <BodyText>
                                <strong>{isEn ? "License/Resolution No:" : "No. Licencia/Resolución:"}</strong> {approvalNumber}
                            </BodyText>
                        )}
                        <BodyText>
                            <strong>{isEn ? "Submission Date:" : "Fecha de Envío:"}</strong> {formattedDate}
                        </BodyText>
                        {!isGridView && formattedUpdatedAt && formattedUpdatedAt !== formattedDate && (
                            <DesktopOnlyMeta>
                                <BodyText>
                                    <strong>{isEn ? "Last Update:" : "Última Actualización:"}</strong> {formattedUpdatedAt}
                                </BodyText>
                            </DesktopOnlyMeta>
                        )}
                    </MetaGrid>

                    <ButtonContainer style={{ width: isGridView ? "100%" : undefined }}>
                        <Button
                            variant="contained"
                            onClick={onClick}
                            fullWidth
                            sx={{
                                bgcolor: "#0F2350",
                                color: "#FFFFFF",
                                borderRadius: "12px",
                                px: 3,
                                py: 1.2,
                                fontFamily: "Roboto, sans-serif",
                                fontWeight: 700,
                                fontSize: "13.5px",
                                letterSpacing: "0.5px",
                                textTransform: "uppercase",
                                boxShadow: "none",
                                transition: "all 0.2s ease",
                                "&:hover": {
                                    bgcolor: "#EA361A",
                                    boxShadow: "none",
                                }
                            }}
                        >
                            {mainButtonText}
                        </Button>
                        {(rolBase === ROLE_TYPE.COMPANY || Number(company_user_rol) === ROLE_TYPE.REPRESENTATIVE) &&
                        statusID != 10 &&
                        statusID != 8 &&
                        statusID != 7 ? (
                            <Button
                                variant="outlined"
                                onClick={() => {
                                    if (setRequestCode) setRequestCode(requestCode);
                                    if (handleCompanySelected) handleCompanySelected(companyID);
                                    if (onClaimModalVisibility) onClaimModalVisibility(true);
                                }}
                                fullWidth
                                sx={{
                                    bgcolor: "transparent",
                                    color: !docIdentification ? "#EA361A" : "#0F2350",
                                    borderColor: !docIdentification ? "#EA361A" : "#CFDCE0",
                                    borderRadius: "12px",
                                    px: 3,
                                    py: 1.2,
                                    fontFamily: "Roboto, sans-serif",
                                    fontWeight: 700,
                                    fontSize: "12.5px",
                                    textTransform: "uppercase",
                                    transition: "all 0.2s ease",
                                    "&:hover": {
                                        bgcolor: !docIdentification ? "rgba(234, 54, 26, 0.15)" : "#F8FAFC",
                                        borderColor: !docIdentification ? "#EA361A" : "#0F2350",
                                    }
                                }}
                            >
                                {!docIdentification 
                                  ? (isEn ? "ASSIGN APPLICANT" : "ASIGNAR SOLICITANTE") 
                                  : (isEn ? "CHANGE APPLICANT" : "CAMBIAR SOLICITANTE")}
                            </Button>
                        ) : null}
                    </ButtonContainer>
                </CardBody>

                {hasPendingAction && (
                    <ActionRequiredMessageBox>
                        <StyledWarningIcon style={{ color: "#C2410C", fontSize: "20px", flexShrink: 0, marginTop: "2px" }} />
                        <div style={{ flex: 1, fontSize: "13px", lineHeight: "1.45" }}>
                            <strong style={{ color: "#9A3412", display: "block", marginBottom: "2px" }}>{isEn ? "Analyst Note:" : "Observación del Analista:"}</strong>
                            <div
                                style={{
                                    color: "#C2410C",
                                    display: "-webkit-box",
                                    WebkitLineClamp: 5,
                                    WebkitBoxOrient: "vertical",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    maxHeight: "7.5em"
                                }}
                                dangerouslySetInnerHTML={{ __html: cleanHTML(requiredActionMessage || "Esta solicitud requiere de tu acción para continuar.") }}
                            />
                        </div>
                    </ActionRequiredMessageBox>
                )}
            </div>

            <ProgressContainer>
                <ProgressHeader>
                    <ProgressStatusText>
                        {statusID === REQUEST_STATUS.REJECTED ? "RECHAZADO" : statusID === REQUEST_STATUS.CANCELED ? "CANCELADO" : status.toUpperCase()}
                    </ProgressStatusText>
                    <ProgressPercentText>
                        {displayPercent}
                    </ProgressPercentText>
                </ProgressHeader>
                <ProgressBarContainer>
                    <ProgressBarPercent variant={currentStatusVariant.variant} percent={displayPercent} />
                </ProgressBarContainer>
            </ProgressContainer>
        </CardContent>
    );

    return hasPendingAction ? (
        <ActionRequiredContainer style={{ height: isGridView ? '100%' : undefined }}>
            {cardContentJSX}
        </ActionRequiredContainer>
    ) : (
        <Container style={{ height: isGridView ? '100%' : undefined }}>
            {cardContentJSX}
        </Container>
    );
}

export default memo(RequestCard);
